import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/api';
import { InventoryRevision, InventoryRevisionItem, InventoryBalanceItem } from '../../types';
import { openAndPrintHtml, renderAndPrintInWindow } from '../../utils/printDocument';
import SemiFinishedAdjustmentPreview, { SemiFinishedAdjustmentTotal } from '../../components/SemiFinishedAdjustmentPreview';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';

const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)', verticalAlign: 'top' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  padding: '8px 16px',
  ...extra,
});

type NewRevisionForm = { name: string; dateFrom: string; dateTo: string; notes: string; isZeroRevision: boolean; warehouseId: string };
type EntryDraft = { pieceCount: string; pieceWeight: string; extraWeight: string; notes: string };
type RevisionImportRow = {
  revisionItemId: number;
  itemType: string;
  itemName: string;
  unit: string;
  currentQuantity: number;
  addedQuantity: number;
};
type RevisionImportPreview = { revisionId: number; version: number; rows: RevisionImportRow[] };
type RevisionExpectedSource = {
  sourceType: string;
  sourceName: string;
  date: string;
  quantityDelta: number;
  included: boolean;
};
type RevisionExpectedChange = {
  revisionItemId: number;
  productId: number | null;
  semiFinishedProductId?: number | null;
  itemType?: string;
  productName: string;
  categoryName: string | null;
  weightType: string;
  previousExpectedQuantity: number;
  expectedQuantity: number;
  sources: RevisionExpectedSource[];
};
type RevisionProductMovement = {
  id: string;
  movementDate: string;
  documentDate: string;
  type: string;
  label: string;
  status: string;
  quantityDelta: number;
  pendingDelta: number | null;
  balanceAfter: number;
  isStockoutTransition: boolean;
  isRevisionBaseline: boolean;
  notes: string;
  writeOffId?: number | null;
  productId?: number | null;
  eventOrderId?: number | null;
};
type RevisionWriteOffDish = {
  sectionName: string;
  dishId: number;
  dishName: string;
  portions: number;
  quantity: number;
  weightType: string;
};
const isPostedRevision = (status: string) => status === 'Posted' || status === 'Confirmed';
const revisionStatusLabel = (status: string) => isPostedRevision(status) ? 'Проведено' : status === 'Cancelled' ? 'Скасовано' : 'Чернетка';
type ExpandedRevisionRow = {
  sourceType: string;
  sourceName: string;
  categoryName: string;
  productName: string;
  b52Code: string;
  b52Name: string;
  finalWeight: number;
  weightType: string;
};
type ExpandedRevisionReport = {
  id: number;
  name: string;
  dateFrom: string;
  dateTo: string;
  status: string;
  rows: ExpandedRevisionRow[];
};
type AvailableOrderForAdjustment = {
  id: number;
  eventDate: string;
  eventName: string;
  customerName: string;
  eventVenue: string;
  guestCount: number;
};
type OrderAdjustmentPreviewRow = {
  sectionName: string;
  dishId: number;
  dishName: string;
  portions: number;
  productId: number;
  productName: string;
  categoryName: string | null;
  weightType: string;
  quantity: number;
};
type OrderAdjustmentPreviewTotal = {
  productId: number;
  productName: string;
  categoryName: string | null;
  weightType: string;
  quantity: number;
};
type OrderAdjustmentPreview = {
  eventOrderId: number;
  eventName: string;
  customerName: string;
  eventDate: string;
  rows: OrderAdjustmentPreviewRow[];
  totals: OrderAdjustmentPreviewTotal[];
  semiFinishedTotals?: SemiFinishedAdjustmentTotal[];
};
type AvailableReceiptForAdjustment = {
  id: number;
  date: string;
  supplierId: number | null;
  supplierName: string | null;
  invoiceNumber: string;
  itemCount: number;
  totalAmount: number;
};
type ReceiptAdjustmentPreviewRow = {
  productId: number;
  productName: string;
  categoryName: string | null;
  weightType: string;
  quantity: number;
};
type ReceiptAdjustmentPreview = {
  receiptId: number;
  date: string;
  supplierName: string | null;
  invoiceNumber: string;
  rows: ReceiptAdjustmentPreviewRow[];
};

const emptyForm = (): NewRevisionForm => ({
  name: `Ревізія ${new Date().toLocaleDateString('uk-UA')}`,
  dateFrom: new Date().toISOString().slice(0, 10),
  dateTo: new Date().toISOString().slice(0, 10),
  notes: '',
  isZeroRevision: false,
  warehouseId: '',
});

const emptyEntryDraft = (): EntryDraft => ({
  pieceCount: '',
  pieceWeight: '',
  extraWeight: '',
  notes: '',
});

const parseNum = (v: string): number => {
  const normalized = v.replace(',', '.').trim();
  if (!normalized) return 0;
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNum = (v: number, digits = 2): string => v.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: digits });
const formatEntryFormula = (entries: InventoryRevisionItem['entries']): string => {
  if (!entries || entries.length === 0) return '';
  return entries
    .map((e, idx) => {
      const absValue = formatNum(Math.abs(e.addedWeight), 3);
      if (idx === 0) return e.addedWeight < 0 ? `-${absValue}` : absValue;
      return e.addedWeight < 0 ? ` - ${absValue}` : ` + ${absValue}`;
    })
    .join('');
};

const InventoryRevisionsPage: React.FC = () => {
  const [revisions, setRevisions] = useState<InventoryRevision[]>([]);
  const [selected, setSelected] = useState<InventoryRevision | null>(null);
  const [actuals, setActuals] = useState<Record<number, string>>({});
  const [entryDrafts, setEntryDrafts] = useState<Record<number, EntryDraft>>({});
  const [balanceMap, setBalanceMap] = useState<Record<number, number>>({});
  const [movementItemId, setMovementItemId] = useState<number | null>(null);
  const [movementsByProduct, setMovementsByProduct] = useState<Record<number, RevisionProductMovement[]>>({});
  const [movementsBySemiFinished, setMovementsBySemiFinished] = useState<Record<number, RevisionProductMovement[]>>({});
  const [movementLoadingProductId, setMovementLoadingProductId] = useState<number | null>(null);
  const [movementLoadingSemiFinishedId, setMovementLoadingSemiFinishedId] = useState<number | null>(null);
  const [movementErrors, setMovementErrors] = useState<Record<number, string>>({});
  const [semiMovementErrors, setSemiMovementErrors] = useState<Record<number, string>>({});
  const [writeOffDishRows, setWriteOffDishRows] = useState<Record<string, RevisionWriteOffDish[]>>({});
  const [writeOffDishLoading, setWriteOffDishLoading] = useState<string | null>(null);
  const [writeOffDishErrors, setWriteOffDishErrors] = useState<Record<string, string>>({});
  const [expandedWriteOffDishes, setExpandedWriteOffDishes] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewRevisionForm>(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingEntryId, setAddingEntryId] = useState<number | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorMobileTab, setEditorMobileTab] = useState<'input' | 'summary'>('input');
  const [isMobileEditor, setIsMobileEditor] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  const [isMobilePage, setIsMobilePage] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= 1100 : false);
  const [expandSemiForReport, setExpandSemiForReport] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedExpandedSemiIds, setSelectedExpandedSemiIds] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportSemiSearch, setExportSemiSearch] = useState('');
  const [exportSemiSort, setExportSemiSort] = useState<'name-asc' | 'name-desc' | 'selected-first'>('name-asc');
  const [syncingItems, setSyncingItems] = useState(false);
  const [refreshingExpected, setRefreshingExpected] = useState(false);
  const [expectedChanges, setExpectedChanges] = useState<RevisionExpectedChange[] | null>(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState<RevisionImportPreview | null>(null);
  const [importValues, setImportValues] = useState<Record<number, string>>({});
  const [importError, setImportError] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [error, setError] = useState<string>('');
  const [showOrderAdjustModal, setShowOrderAdjustModal] = useState(false);
  const [orderQuery, setOrderQuery] = useState('');
  const [orderResults, setOrderResults] = useState<AvailableOrderForAdjustment[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDirection, setOrderDirection] = useState<'add' | 'subtract'>('subtract');
  const [applyingOrder, setApplyingOrder] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [preview, setPreview] = useState<OrderAdjustmentPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showReceiptAdjustModal, setShowReceiptAdjustModal] = useState(false);
  const [receiptQuery, setReceiptQuery] = useState('');
  const [receiptResults, setReceiptResults] = useState<AvailableReceiptForAdjustment[]>([]);
  const [selectedReceiptId, setSelectedReceiptId] = useState<number | null>(null);
  const [receiptDirection, setReceiptDirection] = useState<'add' | 'subtract'>('subtract');
  const [applyingReceipt, setApplyingReceipt] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState<ReceiptAdjustmentPreview | null>(null);
  const [loadingReceiptPreview, setLoadingReceiptPreview] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await api.get<InventoryRevision[]>('/inventoryrevisions');
      setRevisions(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsMobileEditor(window.innerWidth <= 900);
      setIsMobilePage(window.innerWidth <= 1100);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showEditorModal) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === 'f5' || ((e.ctrlKey || e.metaKey) && key === 'r')) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showEditorModal]);

  const hydrateRevisionState = (revision: InventoryRevision) => {
    setSelected(revision);
    const map: Record<number, string> = {};
    const drafts: Record<number, EntryDraft> = {};
    (revision.items || []).forEach((i) => {
      map[i.id] = String(i.actualQuantity);
      drafts[i.id] = emptyEntryDraft();
    });
    setActuals(map);
    setEntryDrafts(drafts);
  };

  const hasUnsavedChanges = useMemo(() => {
    if (!selected?.items) return false;
    return selected.items.some((i) => {
      const localActual = parseNum(actuals[i.id] ?? String(i.actualQuantity));
      return Math.abs(localActual - i.actualQuantity) > 0.0001;
    });
  }, [actuals, selected]);

  const openRevision = async (id: number) => {
    if (showEditorModal && hasUnsavedChanges) {
      setError('Є незбережені зміни у вікні введення ревізії. Спочатку збережіть їх.');
      return;
    }
    setError('');
    const revRes = await api.get<InventoryRevision>(`/inventoryrevisions/${id}`);
    const warehouseParams = revRes.data.warehouseId
      ? { warehouseId: revRes.data.warehouseId }
      : { unassignedOnly: true };
    const balRes = await api.get<InventoryBalanceItem[]>('/inventorybalance', { params: warehouseParams });
    hydrateRevisionState(revRes.data);
    setMovementItemId(null);
    setShowEditorModal(false);
    const bmap: Record<number, number> = {};
    balRes.data.forEach((b) => { bmap[b.productId] = b.balance; });
    setBalanceMap(bmap);
  };

  const toggleItemMovements = async (item: InventoryRevisionItem) => {
    const productId = item.productId;
    const semiFinishedProductId = item.semiFinishedProductId;
    if (!productId && !semiFinishedProductId) return;
    if (movementItemId === item.id) {
      setMovementItemId(null);
      return;
    }
    setMovementItemId(item.id);
    if (productId && movementsByProduct[productId]) return;
    if (semiFinishedProductId && movementsBySemiFinished[semiFinishedProductId]) return;
    try {
      if (productId) {
        setMovementErrors((current) => ({ ...current, [productId]: '' }));
        setMovementLoadingProductId(productId);
        const params = selected?.warehouseId
          ? { warehouseId: selected.warehouseId }
          : { unassignedOnly: true };
        const response = await api.get<RevisionProductMovement[]>(`/inventorybalance/${productId}/movements`, { params });
        setMovementsByProduct((current) => ({ ...current, [productId]: response.data }));
      } else if (semiFinishedProductId) {
        setSemiMovementErrors((current) => ({ ...current, [semiFinishedProductId]: '' }));
        setMovementLoadingSemiFinishedId(semiFinishedProductId);
        const response = await api.get<RevisionProductMovement[]>(`/inventorybalance/semi-finished/${semiFinishedProductId}/movements`);
        setMovementsBySemiFinished((current) => ({ ...current, [semiFinishedProductId]: response.data }));
      }
    } catch {
      const message = 'Не вдалося завантажити рух. Перевірте з’єднання та спробуйте ще раз.';
      if (productId) setMovementErrors((current) => ({ ...current, [productId]: message }));
      if (semiFinishedProductId) setSemiMovementErrors((current) => ({ ...current, [semiFinishedProductId]: message }));
    } finally {
      setMovementLoadingProductId(null);
      setMovementLoadingSemiFinishedId(null);
    }
  };

  const toggleWriteOffDishBreakdown = async (movement: RevisionProductMovement) => {
    if (!movement.writeOffId || !movement.productId) return;
    const key = `${movement.writeOffId}-${movement.productId}`;
    if (expandedWriteOffDishes.has(key)) {
      setExpandedWriteOffDishes((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
      return;
    }
    setExpandedWriteOffDishes((current) => new Set(current).add(key));
    if (writeOffDishRows[key]) return;
    setWriteOffDishErrors((current) => ({ ...current, [key]: '' }));
    setWriteOffDishLoading(key);
    try {
      const response = await api.get<RevisionWriteOffDish[]>(
        `/inventorybalance/write-offs/${movement.writeOffId}/products/${movement.productId}/dish-breakdown`
      );
      setWriteOffDishRows((current) => ({ ...current, [key]: response.data }));
    } catch {
      setWriteOffDishErrors((current) => ({
        ...current,
        [key]: 'Не вдалося завантажити розподіл списання по стравах.',
      }));
    } finally {
      setWriteOffDishLoading(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name,
      dateFrom: new Date(form.dateFrom).toISOString(),
      dateTo: new Date(form.dateTo).toISOString(),
      notes: form.notes,
      isZeroRevision: form.isZeroRevision,
      warehouseId: form.warehouseId ? Number(form.warehouseId) : null,
    };
    const res = await api.post<InventoryRevision>('/inventoryrevisions', payload);
    setShowForm(false);
    setForm(emptyForm());
    await loadList();
    hydrateRevisionState(res.data);
  };

  const handleSaveItems = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const payload = Object.entries(actuals).map(([revisionItemId, actualQuantity]) => ({
        revisionItemId: parseInt(revisionItemId, 10),
        actualQuantity: parseNum(actualQuantity),
      }));
      const res = await api.put<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/items`,
        payload,
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
    } finally {
      setSaving(false);
    }
  };

  const syncMissingItems = async () => {
    if (!selected) return;
    setSyncingItems(true);
    setError('');
    setSyncMessage('');
    try {
      const res = await api.post<{ added: number; revision: InventoryRevision }>(
        `/inventoryrevisions/${selected.id}/sync-items`,
        {},
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      // Keep unsaved typed quantities for positions that were already in the revision.
      const unsavedActuals = { ...actuals };
      const unsavedDrafts = { ...entryDrafts };
      hydrateRevisionState(res.data.revision);
      setActuals(prev => ({ ...prev, ...unsavedActuals }));
      setEntryDrafts(prev => ({ ...prev, ...unsavedDrafts }));
      await loadList();
      setSyncMessage(res.data.added > 0
        ? `Додано нових позицій: ${res.data.added}.`
        : 'Нових позицій немає — ревізія вже містить усі продукти та Н/Ф.');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося додати нові позиції.');
    } finally {
      setSyncingItems(false);
    }
  };

  const refreshExpected = async () => {
    if (!selected) return;
    if (!window.confirm(`Перерахувати очікувані залишки продуктів і Н/Ф станом на ${new Date(selected.dateTo).toLocaleDateString('uk-UA')} включно? Накладні, виробництво, списання й коригування з пізнішою датою не враховуватимуться. Зважені (фактичні) кількості не зміняться.`)) return;
    setRefreshingExpected(true);
    setExpectedChanges(null);
    setError('');
    setSyncMessage('');
    try {
      const res = await api.post<{ changed: number; changedItems: RevisionExpectedChange[]; revision: InventoryRevision }>(
        `/inventoryrevisions/${selected.id}/refresh-expected`,
        {},
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      setExpectedChanges(res.data.changedItems);
      const unsavedActuals = { ...actuals };
      const unsavedDrafts = { ...entryDrafts };
      hydrateRevisionState(res.data.revision);
      setActuals(prev => ({ ...prev, ...unsavedActuals }));
      setEntryDrafts(prev => ({ ...prev, ...unsavedDrafts }));
      await loadList();
      setSyncMessage(res.data.changed > 0
        ? `Очікуване оновлено для позицій: ${res.data.changed}. Перегляньте деталі перерахунку.`
        : 'Очікувані значення вже актуальні.');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося перерахувати очікуване.');
    } finally {
      setRefreshingExpected(false);
    }
  };

  const downloadImportTemplate = async () => {
    if (!selected) return;
    setImportBusy(true);
    setImportError('');
    try {
      const res = await api.get(`/inventoryrevisions/${selected.id}/import-template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `revision-${selected.id}-import-template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      setImportError(e?.response?.data?.message || 'Не вдалося завантажити шаблон.');
    } finally {
      setImportBusy(false);
    }
  };

  const previewImportFile = async (file: File) => {
    if (!selected) return;
    setImportBusy(true);
    setImportError('');
    setImportPreview(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await api.post<RevisionImportPreview>(`/inventoryrevisions/${selected.id}/import-preview`, data);
      setImportPreview(res.data);
      setImportValues(Object.fromEntries(res.data.rows.map(row => [row.revisionItemId, String(row.addedQuantity)])));
    } catch (e: any) {
      const details = e?.response?.data?.errors;
      setImportError([e?.response?.data?.message || 'Не вдалося прочитати файл.', ...(Array.isArray(details) ? details : [])].join('\n'));
    } finally {
      setImportBusy(false);
    }
  };

  const applyImport = async () => {
    if (!selected || !importPreview) return;
    const items = importPreview.rows.map(row => ({
      revisionItemId: row.revisionItemId,
      addedQuantity: Number((importValues[row.revisionItemId] || '').trim().replace(',', '.')),
    }));
    if (items.some(item => !Number.isFinite(item.addedQuantity) || item.addedQuantity < 0 ||
      !/^\d+(?:[.,]\d+)?$/.test((importValues[item.revisionItemId] || '').trim()))) {
      setImportError('Усі кількості мають бути невід’ємними числами. Для нуля введіть 0.');
      return;
    }
    setImportBusy(true);
    setImportError('');
    try {
      const res = await api.put<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/import-items`,
        { items },
        { headers: { 'If-Match': `"${importPreview.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
      setShowImport(false);
      setImportPreview(null);
    } catch (e: any) {
      setImportError(e?.response?.data?.message || 'Не вдалося зберегти значення ревізії.');
    } finally {
      setImportBusy(false);
    }
  };

  const handleAddEntry = async (item: InventoryRevisionItem) => {
    if (!selected) return;
    const draft = entryDrafts[item.id] || emptyEntryDraft();
    const payload = {
      pieceCount: parseNum(draft.pieceCount),
      pieceWeight: parseNum(draft.pieceWeight),
      packageCount: 0,
      packageWeight: 0,
      extraWeight: parseNum(draft.extraWeight),
      notes: draft.notes || '',
    };

    const computed = payload.pieceCount * payload.pieceWeight + payload.extraWeight;
    if (Math.abs(computed) < 0.0000001) {
      setError(`"${item.itemName || 'Позиція'}": додана вага не може бути 0.`);
      return;
    }

    setAddingEntryId(item.id);
    setError('');
    try {
      const res = await api.post<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/items/${item.id}/entries`,
        payload,
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
      setSearch((s) => s);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Не вдалося додати запис.';
      setError(message);
    } finally {
      setAddingEntryId(null);
    }
  };

  const handleExportExcel = () => {
    if (!selected) return;
    const semiItems = (selected.items || []).filter(item => item.semiFinishedProductId != null);
    setSelectedExpandedSemiIds(new Set(expandSemiForReport
      ? semiItems.map(item => item.semiFinishedProductId as number)
      : []));
    setExportSemiSearch('');
    setExportSemiSort('name-asc');
    setShowExportOptions(true);
  };

  const confirmExportExcel = async () => {
    if (!selected) return;
    setExporting(true);
    setError('');
    try {
      const res = await api.get(`/inventoryrevisions/${selected.id}/export-excel`, {
        params: {
          selectiveSemiFinished: true,
          expandSemiFinishedIds: Array.from(selectedExpandedSemiIds).join(','),
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-revision-${selected.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExportOptions(false);
    } catch (e: any) {
      const message = e?.response?.data?.message || '\u041d\u0435 \u0432\u0434\u0430\u043b\u043e\u0441\u044f \u0441\u0444\u043e\u0440\u043c\u0443\u0432\u0430\u0442\u0438 Excel-\u0444\u0430\u0439\u043b.';
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const handlePrintExpanded = async () => {
    if (!selected) return;
    setError('');
    const printWindow = await openAndPrintHtml(
      '<html><body style="font-family:Arial,sans-serif;padding:16px;color:#2c3e50;">Формування друку...</body></html>',
      {
        autoPrint: false,
        windowFeatures: 'width=1200,height=800',
        onPopupBlocked: () => setError('Браузер заблокував вікно друку. Дозвольте pop-up для цього сайту.'),
        onError: () => setError('Не вдалося сформувати друк.'),
      },
    );
    if (!printWindow) {
      return;
    }

    try {
      const res = await api.get<ExpandedRevisionReport>(`/inventoryrevisions/${selected.id}/expanded-report`, {
        headers: { 'X-Report-Mode': expandSemiForReport ? 'expanded' : 'flat' },
        params: { reportMode: expandSemiForReport ? 'expanded' : 'flat' },
      });
      const report = res.data;
      const rows = report.rows || [];
      if (rows.length === 0) {
        printWindow.close();
        setError('Немає даних для друку.');
        return;
      }

      const escapeHtml = (value: string): string =>
        value
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const grouped = rows.reduce<Record<string, ExpandedRevisionRow[]>>((acc, row) => {
        const key = row.sourceType || 'Інше';
        acc[key] = acc[key] || [];
        acc[key].push(row);
        return acc;
      }, {});

      const blocksHtml = Object.keys(grouped)
        .sort((a, b) => a.localeCompare(b, 'uk'))
        .map((sourceType) => {
          const sectionRows = grouped[sourceType]
            .slice()
            .sort((a, b) =>
              a.sourceName.localeCompare(b.sourceName, 'uk')
              || a.categoryName.localeCompare(b.categoryName, 'uk')
              || a.productName.localeCompare(b.productName, 'uk')
            )
            .map((r) => `<tr>
              <td>${escapeHtml(r.sourceName || '—')}</td>
              <td>${escapeHtml(r.categoryName || '—')}</td>
              <td>${escapeHtml(r.productName || '—')}</td>
              <td>${escapeHtml(r.b52Code || '—')}</td>
              <td>${escapeHtml(r.b52Name || '—')}</td>
              <td style="text-align:right;">${Number(r.finalWeight || 0).toFixed(3)}</td>
              <td style="text-align:center;">${escapeHtml(r.weightType || 'kg')}</td>
            </tr>`)
            .join('');

          return `
            <h3 style="margin:16px 0 8px; color:#2c3e50;">${escapeHtml(sourceType)}</h3>
            <table style="width:100%; border-collapse:collapse; margin-bottom:14px; table-layout:fixed;">
              <thead>
                <tr>
                  <th style="text-align:left;">Джерело</th>
                  <th style="text-align:left;">Категорія</th>
                  <th style="text-align:left;">Інгредієнт</th>
                  <th style="text-align:left;">Код B52</th>
                  <th style="text-align:left;">Назва B52</th>
                  <th style="text-align:right;">Кінцева вага</th>
                  <th style="text-align:center;">Од.</th>
                </tr>
              </thead>
              <tbody>${sectionRows}</tbody>
            </table>
          `;
        })
        .join('');

      const rendered = await renderAndPrintInWindow(printWindow, `<!doctype html><html lang="uk"><head><meta charset="utf-8" />
      <title>Ревізія ${report.id}${expandSemiForReport ? ' — розклад Н/Ф' : ''}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#2c3e50;}
        h1{margin:0 0 6px;}
        p{margin:4px 0;color:#4b5563;font-size:12px;}
        th,td{border:1px solid #d1d5db;padding:6px 8px;font-size:12px;vertical-align:top;word-break:break-word;}
        th{background:#f3f4f6;}
      </style>
      </head><body>
        <h1>${escapeHtml(report.name)} (ревізія #${report.id})</h1>
        <p>Період: ${new Date(report.dateFrom).toLocaleDateString('uk-UA')} — ${new Date(report.dateTo).toLocaleDateString('uk-UA')}</p>
        <p>Статус: ${escapeHtml(report.status || '—')}</p>
        <p>Сформовано: ${new Date().toLocaleString('uk-UA')}</p>
        ${blocksHtml}
      </body></html>`, {
        minimumDelayMs: 250,
        onError: () => setError('Не вдалося підготувати друк.'),
      });
      if (!rendered) return;
    } catch (e: any) {
      printWindow.close();
      const message = e?.response?.data?.message || 'Не вдалося сформувати друк.';
      setError(message);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    if (!window.confirm('Підтвердити ревізію? Перед проведенням очікуване буде перераховано на кінець дати «Період по». Після підтвердження ревізія стане базою для розрахунку залишків.')) return;
    try {
      const res = await api.post<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/confirm`,
        null,
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося провести ревізію. Оновіть документ і повторіть дію.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити ревізію?')) return;
    const revision = revisions.find(item => item.id === id);
    if (!revision) return;
    await api.delete(`/inventoryrevisions/${id}`, { headers: { 'If-Match': `"${revision.version}"` } });
    if (selected?.id === id) setSelected(null);
    await loadList();
  };

  const loadOrdersForAdjustment = async (query: string) => {
    setLoadingOrders(true);
    try {
      const res = await api.get<AvailableOrderForAdjustment[]>('/inventoryrevisions/available-orders', {
        params: query.trim() ? { query: query.trim() } : undefined,
      });
      setOrderResults(res.data);
    } catch {
      setOrderResults([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const openOrderAdjustModal = () => {
    setSelectedOrderId(null);
    setOrderDirection('subtract');
    setOrderQuery('');
    setPreview(null);
    setShowOrderAdjustModal(true);
    loadOrdersForAdjustment('');
  };

  useEffect(() => {
    if (!showOrderAdjustModal || !selectedOrderId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setError('');
    api.get<OrderAdjustmentPreview>(`/inventoryrevisions/order-adjustment-preview/${selectedOrderId}`, {
      params: { direction: orderDirection },
    })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch((e: any) => {
        if (cancelled) return;
        setPreview(null);
        setError(e?.response?.data?.message || 'Не вдалося сформувати попередній перегляд.');
      })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [selectedOrderId, orderDirection, showOrderAdjustModal]);

  const handleApplyOrder = async () => {
    if (!selected || !selectedOrderId) return;
    setApplyingOrder(true);
    setError('');
    try {
      const res = await api.post<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/apply-order`,
        { eventOrderId: selectedOrderId, direction: orderDirection },
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
      setShowOrderAdjustModal(false);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Не вдалося застосувати коригування по замовленню.';
      setError(message);
    } finally {
      setApplyingOrder(false);
    }
  };

  const loadReceiptsForAdjustment = async (query: string) => {
    setLoadingReceipts(true);
    try {
      const res = await api.get<AvailableReceiptForAdjustment[]>('/inventoryrevisions/available-receipts', {
        params: query.trim() ? { query: query.trim() } : undefined,
      });
      setReceiptResults(res.data);
    } catch {
      setReceiptResults([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const openReceiptAdjustModal = () => {
    setSelectedReceiptId(null);
    setReceiptDirection('subtract');
    setReceiptQuery('');
    setReceiptPreview(null);
    setShowReceiptAdjustModal(true);
    loadReceiptsForAdjustment('');
  };

  useEffect(() => {
    if (!showReceiptAdjustModal || !selectedReceiptId) {
      setReceiptPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingReceiptPreview(true);
    setError('');
    api.get<ReceiptAdjustmentPreview>(`/inventoryrevisions/receipt-adjustment-preview/${selectedReceiptId}`, {
      params: { direction: receiptDirection },
    })
      .then((res) => { if (!cancelled) setReceiptPreview(res.data); })
      .catch((e: any) => {
        if (cancelled) return;
        setReceiptPreview(null);
        setError(e?.response?.data?.message || 'Не вдалося сформувати попередній перегляд по накладній.');
      })
      .finally(() => { if (!cancelled) setLoadingReceiptPreview(false); });
    return () => { cancelled = true; };
  }, [selectedReceiptId, receiptDirection, showReceiptAdjustModal]);

  const handleApplyReceipt = async () => {
    if (!selected || !selectedReceiptId) return;
    setApplyingReceipt(true);
    setError('');
    try {
      const res = await api.post<InventoryRevision>(
        `/inventoryrevisions/${selected.id}/apply-receipt`,
        { receiptId: selectedReceiptId, direction: receiptDirection },
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      hydrateRevisionState(res.data);
      await loadList();
      setShowReceiptAdjustModal(false);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Не вдалося застосувати коригування по накладній.';
      setError(message);
    } finally {
      setApplyingReceipt(false);
    }
  };

  const handleCloseEditorModal = () => {
    if (hasUnsavedChanges) {
      setError('Неможливо закрити вікно без збереження. Натисніть "Зберегти".');
      return;
    }
    setShowEditorModal(false);
    setError('');
  };

  const filteredItems = useMemo(() => {
    if (!selected?.items) return [];
    const query = search.trim().toLowerCase();
    if (!query) return selected.items;
    return selected.items.filter((i) =>
      [i.itemName, i.categoryName, i.itemType]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(query))
    );
  }, [search, selected]);

  return (
    <div style={{ padding: isMobilePage ? '12px' : '24px', maxWidth: '100%', margin: '0 auto', overflowX: 'hidden' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Ревізії складу</h2>

      {error && (
        <div role="alert" style={{ marginTop: '12px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: '6px', padding: '10px 12px' }}>
          {error}
        </div>
      )}
      {syncMessage && !showEditorModal && (
        <div role="status" style={{ marginTop: '12px', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: '6px', padding: '10px 12px' }}>
          {syncMessage}
        </div>
      )}
      {expectedChanges && expectedChanges.length > 0 && (
        <div role="dialog" aria-modal="true" aria-labelledby="expected-changes-title" style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div className="inventory-revision-dialog" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', width: 'min(900px, 100%)', maxHeight: '88vh', overflow: 'auto', padding: '20px', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <h3 id="expected-changes-title" style={{ margin: '0 0 6px', color: 'var(--crm-text)' }}>Деталі перерахунку очікуваних залишків</h3>
                <p style={{ margin: 0, color: 'var(--crm-text-muted)', fontSize: '13px' }}>
                  Змінено позицій: {expectedChanges.length}. Розрахунок станом на {selected ? new Date(selected.dateTo).toLocaleDateString('uk-UA') : ''} включно: враховано проведену базову ревізію та рухи до цієї дати; пізніші документи виключені. Фактичні зважені значення не змінювалися.
                </p>
              </div>
              <button type="button" onClick={() => setExpectedChanges(null)} style={btn('muted', { flexShrink: 0 })}>Закрити</button>
            </div>
            <div style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
              {expectedChanges.map(change => (
                <section key={change.revisionItemId} style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '12px', background: 'var(--crm-surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                    <strong style={{ color: 'var(--crm-text)' }}>
                      {change.itemType === 'semi-finished' ? 'Н/Ф · ' : ''}{change.productName}{change.categoryName ? <span style={{ color: 'var(--crm-text-muted)', fontWeight: 400 }}> ({change.categoryName})</span> : null}
                    </strong>
                    <span style={{ fontSize: '13px', textAlign: 'right' }}>
                      {formatNum(change.previousExpectedQuantity, 3)} → <strong>{formatNum(change.expectedQuantity, 3)} {change.weightType}</strong>
                    </span>
                  </div>
                  {change.sources.length > 0 ? (
                    <div style={{ display: 'grid', gap: '5px', marginTop: '9px' }}>
                      {change.sources.map((source, index) => (
                        <div key={`${source.sourceType}-${source.date}-${index}`} className="inventory-revision-expected-source" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', padding: '7px 9px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderRadius: '5px', fontSize: '12px' }}>
                          <span>
                            <span style={{ color: 'var(--crm-text-muted)' }}>{new Date(source.date).toLocaleDateString('uk-UA')} · </span>
                            {source.sourceName}
                            <span className={source.included ? 'inventory-revision-expected-included' : 'inventory-revision-expected-excluded'} style={{ marginLeft: 6, fontWeight: 600 }}>
                              {source.included ? 'Враховано' : 'Не враховано — після дати ревізії'}
                            </span>
                          </span>
                          <strong className={source.included ? source.quantityDelta < 0 ? 'inventory-revision-expected-negative' : 'inventory-revision-expected-positive' : ''} style={{ whiteSpace: 'nowrap', color: source.included ? undefined : 'var(--crm-text-muted)' }}>
                            {source.included && source.quantityDelta > 0 ? '+' : ''}{formatNum(source.quantityDelta, 3)} {change.weightType}
                          </strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <p style={{ margin: '8px 0 0', color: 'var(--crm-text-muted)', fontSize: '12px' }}>Немає проведеної базової ревізії або рухів запасу для цієї позиції.</p>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: isMobilePage ? 'column' : 'row', gap: isMobilePage ? '12px' : '24px', marginTop: '16px' }}>
        <div style={{ flex: isMobilePage ? '1 1 auto' : '0 0 380px', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button onClick={() => setShowForm(true)} style={btn('success', { flex: 1 })}>+ Нова ревізія</button>
            <button
              onClick={() => {
                setShowForm(true);
                setForm({
                  ...emptyForm(),
                  name: `Нульова ревізія ${new Date().toLocaleDateString('uk-UA')}`,
                  isZeroRevision: true,
                });
              }}
              style={btn('accent')}
            >
              0 ревізія
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleCreate} style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '12px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '12px' }}>
              <label>Назва:
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ display: 'block', width: '100%', padding: '6px', marginBottom: '8px' }} />
              </label>
              <label>Період з:
                <input type="date" required value={form.dateFrom} onChange={(e) => setForm({ ...form, dateFrom: e.target.value })} style={{ display: 'block', width: '100%', padding: '6px', marginBottom: '8px' }} />
              </label>
              <label>Період по:
                <input type="date" required value={form.dateTo} onChange={(e) => setForm({ ...form, dateTo: e.target.value })} style={{ display: 'block', width: '100%', padding: '6px', marginBottom: '8px' }} />
              </label>
              <InventoryWarehouseSelect value={form.warehouseId} onChange={(warehouseId) => setForm({ ...form, warehouseId })} />
              <label>Примітки:
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ display: 'block', width: '100%', padding: '6px', marginBottom: '8px' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <input type="checkbox" checked={form.isZeroRevision} onChange={(e) => setForm({ ...form, isZeroRevision: e.target.checked })} />
                Нульова ревізія (усі залишки = 0)
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('success')}>Створити</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('muted')}>Скасувати</button>
              </div>
            </form>
          )}

          <div style={{ maxHeight: isMobilePage ? '320px' : '700px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Назва</th>
                  <th style={thStyle}>Період</th>
                  <th style={thStyle}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((r) => (
                  <tr key={r.id} onClick={() => openRevision(r.id)} style={{ cursor: 'pointer', background: selected?.id === r.id ? 'var(--crm-info-bg)' : undefined }}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{new Date(r.dateFrom).toLocaleDateString('uk-UA')} — {new Date(r.dateTo).toLocaleDateString('uk-UA')}</td>
                    <td style={{ ...tdStyle, color: isPostedRevision(r.status) ? 'var(--crm-success)' : 'var(--crm-warning)' }}>{revisionStatusLabel(r.status)}</td>
                  </tr>
                ))}
                {revisions.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Ревізій немає'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>{selected.name}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button onClick={handleExportExcel} style={btn('primary')}>Експорт Excel</button>
                  <button onClick={handlePrintExpanded} style={btn('accent')}>Друк + B52</button>
                  {!isPostedRevision(selected.status) && (
                    <>
                      {selected.status === 'Draft' && <button onClick={() => { setShowImport(true); setImportPreview(null); setImportError(''); }} style={btn('info')}>Імпорт із файла</button>}
                      <button onClick={() => { setEditorMobileTab('input'); setShowEditorModal(true); }} style={btn('accent')}>Введення ревізії</button>
                      <button onClick={syncMissingItems} disabled={syncingItems} title="Додати в цю ревізію продукти та Н/Ф, створені після її створення" style={btn('info')}>{syncingItems ? 'Додавання...' : '+ Нові позиції'}</button>
                      <button onClick={refreshExpected} disabled={refreshingExpected} title="Оновити очікувані залишки продуктів і Н/Ф з урахуванням рухів, внесених після створення ревізії" style={btn('accent')}>{refreshingExpected ? 'Перерахунок...' : '↻ Перерахувати очікуване'}</button>
                      <button onClick={openOrderAdjustModal} style={btn('warning')}>Коригування за замовленням</button>
                      <button onClick={openReceiptAdjustModal} style={btn('success')}>Коригування за накладною</button>
                      <button onClick={handleConfirm} style={btn('success')}>Підтвердити</button>
                      <button onClick={() => handleDelete(selected.id)} style={btn('danger')}>Видалити</button>
                    </>
                  )}
                </div>
              </div>
              <p>Період: {new Date(selected.dateFrom).toLocaleDateString('uk-UA')} — {new Date(selected.dateTo).toLocaleDateString('uk-UA')}</p>
              <p>Статус: <strong style={{ color: isPostedRevision(selected.status) ? 'var(--crm-success)' : 'var(--crm-warning)' }}>{revisionStatusLabel(selected.status)}</strong></p>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', margin: '6px 0 10px', color: 'var(--crm-text)', fontSize: '13px', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={expandSemiForReport}
                  onChange={(e) => setExpandSemiForReport(e.target.checked)}
                />
                Розкладати Н/Ф на інгредієнти (для друку та Excel)
              </label>
              {showExportOptions && (
                <div role="dialog" aria-modal="true" aria-labelledby="revision-export-title" style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                  <div className="inventory-revision-dialog" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', width: 'min(620px, 100%)', maxHeight: '85vh', overflow: 'auto', padding: '20px', boxShadow: 'var(--crm-shadow-sm)' }}>
                    <h3 id="revision-export-title" style={{ marginTop: 0 }}>Параметри експорту Excel</h3>
                    <p style={{ color: 'var(--crm-text-muted)' }}>Позначені напівфабрикати будуть розкладені на інгредієнти. Непозначені залишаться окремими позиціями Н/Ф.</p>
                    {(() => {
                      const semiItems = (selected.items || []).filter(item => item.semiFinishedProductId != null);
                      if (semiItems.length === 0) return <p>У цій ревізії немає напівфабрикатів для вибору.</p>;
                      return (
                        <>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <input
                              type="search"
                              value={exportSemiSearch}
                              onChange={(event) => setExportSemiSearch(event.target.value)}
                              placeholder="Пошук напівфабрикату..."
                              aria-label="Пошук напівфабрикату"
                              style={{ flex: '1 1 220px', minWidth: 0, padding: '7px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
                            />
                            <select value={exportSemiSort} onChange={(event) => setExportSemiSort(event.target.value as typeof exportSemiSort)} aria-label="Сортування напівфабрикатів" style={{ flex: '0 1 220px', padding: '7px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}>
                              <option value="name-asc">Назва: А—Я</option>
                              <option value="name-desc">Назва: Я—А</option>
                              <option value="selected-first">Розкладати першими</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => setSelectedExpandedSemiIds(previous => new Set(Array.from(previous).concat(semiItems.filter(item => (item.itemName || '').toLocaleLowerCase('uk').includes(exportSemiSearch.trim().toLocaleLowerCase('uk'))).map(item => item.semiFinishedProductId as number))))} style={btn('info', { padding: '6px 10px' })}>Обрати відібрані</button>
                            <button type="button" onClick={() => setSelectedExpandedSemiIds(previous => {
                              const next = new Set(previous);
                              semiItems.filter(item => (item.itemName || '').toLocaleLowerCase('uk').includes(exportSemiSearch.trim().toLocaleLowerCase('uk'))).forEach(item => next.delete(item.semiFinishedProductId as number));
                              return next;
                            })} style={btn('muted', { padding: '6px 10px' })}>Зняти відмітки з відібраних</button>
                          </div>
                          <div style={{ color: 'var(--crm-text-muted)', fontSize: '13px', marginBottom: '6px' }}>
                            Знайдено: {semiItems.filter(item => (item.itemName || '').toLocaleLowerCase('uk').includes(exportSemiSearch.trim().toLocaleLowerCase('uk'))).length} / {semiItems.length}; обрано для розкладання: {selectedExpandedSemiIds.size}
                          </div>
                          <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid var(--crm-border)', borderRadius: '6px' }}>
                            {semiItems
                              .filter(item => (item.itemName || '').toLocaleLowerCase('uk').includes(exportSemiSearch.trim().toLocaleLowerCase('uk')))
                              .sort((a, b) => {
                                if (exportSemiSort === 'selected-first') {
                                  const selectedDifference = Number(selectedExpandedSemiIds.has(b.semiFinishedProductId as number)) - Number(selectedExpandedSemiIds.has(a.semiFinishedProductId as number));
                                  if (selectedDifference !== 0) return selectedDifference;
                                }
                                const nameDifference = (a.itemName || '').localeCompare(b.itemName || '', 'uk', { sensitivity: 'base' });
                                return exportSemiSort === 'name-desc' ? -nameDifference : nameDifference;
                              })
                              .map(item => {
                              const semiId = item.semiFinishedProductId as number;
                              return (
                                <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderBottom: '1px solid var(--crm-table-row-border)' }}>
                                  <input type="checkbox" checked={selectedExpandedSemiIds.has(semiId)} onChange={(event) => setSelectedExpandedSemiIds(previous => {
                                    const next = new Set(previous);
                                    if (event.target.checked) next.add(semiId); else next.delete(semiId);
                                    return next;
                                  })} />
                                  <span>{item.itemName || `Н/Ф #${semiId}`}</span>
                                  <span style={{ marginLeft: 'auto', color: 'var(--crm-text-muted)' }}>{item.actualQuantity.toLocaleString('uk-UA')} {item.weightType || ''}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                      <button type="button" disabled={exporting} onClick={() => setShowExportOptions(false)} style={btn('muted')}>Скасувати</button>
                      <button type="button" disabled={exporting} onClick={confirmExportExcel} style={btn('success')}>{exporting ? 'Формування...' : 'Експортувати Excel'}</button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '12px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Пошук продукту / Н/Ф / категорії..."
                  style={{ width: isMobilePage ? '100%' : '380px', maxWidth: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
                />
                <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>Позицій: {filteredItems.length}</span>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobilePage ? '640px' : '920px' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Назва</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>
                        Мин. ревізія
                        {selected.previousRevisionName || selected.previousRevisionDateTo
                          ? <div style={{ fontSize: 10, fontWeight: 400 }}>
                              {selected.previousRevisionName ? `${selected.previousRevisionName} · ` : ''}
                              {selected.previousRevisionDateTo ? new Date(selected.previousRevisionDateTo).toLocaleDateString('uk-UA') : ''}
                            </div>
                          : null}
                      </th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Очікується</th>
                      <th style={thStyle}>Записи ваги</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Фактично</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Поточний залишок</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Розбіжність</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((i) => {
                      const actual = i.actualQuantity;
                      const discrepancy = i.discrepancy;
                      const currentBalance = i.productId ? (balanceMap[i.productId] ?? null) : null;
                      const formula = formatEntryFormula(i.entries || []);

                      return (
                        <React.Fragment key={i.id}>
                        <tr>
                          <td style={tdStyle}>
                            <div>{i.itemName || '—'}</div>
                            {(i.productId || i.semiFinishedProductId) && (
                              <button
                                type="button"
                                onClick={() => toggleItemMovements(i)}
                                aria-expanded={movementItemId === i.id}
                                style={{ marginTop: 5, padding: '4px 8px', border: '1px solid var(--crm-info)', borderRadius: 5, cursor: 'pointer', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', fontSize: 11 }}
                              >
                                {movementItemId === i.id ? 'Сховати рух' : `Переглянути рух ${i.semiFinishedProductId ? 'Н/Ф' : 'товару'}`}
                              </button>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: i.previousRevisionActualQuantity == null ? 'var(--crm-text-muted)' : undefined }}>
                            {i.previousRevisionActualQuantity == null ? '—' : i.previousRevisionActualQuantity.toFixed(2)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{i.expectedQuantity.toFixed(2)}</td>
                          <td style={{ ...tdStyle, fontSize: '12px', lineHeight: 1.35, maxWidth: '250px' }}>
                            {formula || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{actual.toFixed(2)}</td>
                          <td style={{
                            ...tdStyle, textAlign: 'right', fontWeight: 'bold',
                            color: currentBalance === null ? 'var(--crm-text-muted)' : currentBalance > 0 ? 'var(--crm-success)' : currentBalance < 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)',
                          }}>
                            {currentBalance !== null ? currentBalance.toFixed(2) : '—'}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: discrepancy > 0 ? 'var(--crm-success)' : discrepancy < 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                            {discrepancy.toFixed(2)}
                          </td>
                        </tr>
                        {movementItemId === i.id && (i.productId || i.semiFinishedProductId) && (
                          <tr>
                            <td colSpan={6} style={{ padding: 12, background: 'var(--crm-info-bg)' }}>
                              <strong style={{ display: 'block', marginBottom: 8, color: 'var(--crm-info)' }}>
                                Рух {i.semiFinishedProductId ? 'напівфабрикату' : 'товару'} за період ревізії · {i.itemName} ({new Date(selected.dateFrom).toLocaleDateString('uk-UA')} — {new Date(selected.dateTo).toLocaleDateString('uk-UA')})
                              </strong>
                              {i.semiFinishedProductId && <div style={{ marginBottom: 8, color: 'var(--crm-text-muted)', fontSize: 12 }}>
                                Показано списання саме готового Н/Ф зі складу. Якщо його залишку не вистачило, нестача списується інгредієнтами за рецептом і відображається в русі відповідних товарів.
                              </div>}
                              {(i.productId && movementLoadingProductId === i.productId) || (i.semiFinishedProductId && movementLoadingSemiFinishedId === i.semiFinishedProductId) ? (
                                <div>Завантаження журналу руху…</div>
                              ) : (i.productId && movementErrors[i.productId]) || (i.semiFinishedProductId && semiMovementErrors[i.semiFinishedProductId]) ? (
                                <div role="alert" style={{ color: 'var(--crm-danger)' }}>{i.productId ? movementErrors[i.productId] : semiMovementErrors[i.semiFinishedProductId!]}</div>
                              ) : (() => {
                                const movementRows = i.productId
                                  ? movementsByProduct[i.productId] || []
                                  : movementsBySemiFinished[i.semiFinishedProductId!] || [];
                                const allMovements = [...movementRows].sort((a, b) =>
                                  a.movementDate.localeCompare(b.movementDate) ||
                                  Number(a.isRevisionBaseline) - Number(b.isRevisionBaseline)
                                );
                                const startDate = selected.dateFrom.slice(0, 10);
                                const endDate = selected.dateTo.slice(0, 10);
                                const inPeriod = allMovements
                                  .filter((movement) => movement.movementDate.slice(0, 10) >= startDate && movement.movementDate.slice(0, 10) <= endDate);
                                const targetRevisionLabel = `Ревізія #${selected.id}:`;
                                const targetBaselineIndex = inPeriod.findIndex((movement) => movement.isRevisionBaseline && movement.label.startsWith(targetRevisionLabel));
                                const targetBaseline = targetBaselineIndex >= 0 ? inPeriod[targetBaselineIndex] : null;
                                const openingMovement = [...allMovements].reverse().find((movement) => movement.movementDate.slice(0, 10) < startDate);
                                const expectedBeforeCount = targetBaseline
                                  ? targetBaselineIndex > 0
                                    ? inPeriod[targetBaselineIndex - 1].balanceAfter
                                    : openingMovement?.balanceAfter ?? null
                                  : [...inPeriod].reverse().find((movement) => !movement.isRevisionBaseline)?.balanceAfter
                                    ?? openingMovement?.balanceAfter
                                    ?? null;
                                const actualCount = i.actualQuantity;
                                const countDifference = expectedBeforeCount === null ? null : actualCount - expectedBeforeCount;
                                const periodMovements = inPeriod.filter((movement) => movement !== targetBaseline);
                                const postedMovements = periodMovements.filter((movement) =>
                                  movement.status.toLowerCase() !== 'draft' && movement.status.toLowerCase() !== 'unposted'
                                );
                                const receivedQuantity = postedMovements
                                  .filter((movement) => movement.type === 'receipt' && movement.quantityDelta > 0)
                                  .reduce((total, movement) => total + movement.quantityDelta, 0);
                                const realizedQuantity = postedMovements
                                  .filter((movement) => movement.type === 'writeoff' && movement.eventOrderId != null)
                                  .reduce((total, movement) => total + Math.abs(movement.quantityDelta), 0);
                                const usedQuantity = postedMovements
                                  .filter((movement) =>
                                    (movement.type === 'writeoff' && movement.eventOrderId == null) ||
                                    (movement.type === 'adjustment' && movement.quantityDelta < 0)
                                  )
                                  .reduce((total, movement) => total + Math.abs(movement.quantityDelta), 0);
                                const producedQuantity = i.semiFinishedProductId
                                  ? postedMovements
                                    .filter((movement) => movement.type === 'production' && movement.quantityDelta > 0)
                                    .reduce((total, movement) => total + movement.quantityDelta, 0)
                                  : 0;
                                const writeOffsByDay = new Map<string, RevisionProductMovement[]>();
                                periodMovements.forEach((movement) => {
                                  if (movement.type !== 'writeoff' || movement.status.toLowerCase() === 'draft') return;
                                  const day = movement.movementDate.slice(0, 10);
                                  const dayWriteOffs = writeOffsByDay.get(day) || [];
                                  dayWriteOffs.push(movement);
                                  writeOffsByDay.set(day, dayWriteOffs);
                                });
                                const displayedMovements: Array<
                                  { kind: 'movement'; movement: RevisionProductMovement } |
                                  { kind: 'writeoff-day'; day: string; movements: RevisionProductMovement[] }
                                > = [];
                                const renderedWriteOffDays = new Set<string>();
                                periodMovements.forEach((movement) => {
                                  if (movement.type === 'writeoff' && movement.status.toLowerCase() !== 'draft') {
                                    const day = movement.movementDate.slice(0, 10);
                                    if (!renderedWriteOffDays.has(day)) {
                                      renderedWriteOffDays.add(day);
                                      displayedMovements.push({ kind: 'writeoff-day', day, movements: writeOffsByDay.get(day) || [] });
                                    }
                                  } else {
                                    displayedMovements.push({ kind: 'movement', movement });
                                  }
                                });
                                return (
                                  <div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                                      <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-success-bg)', border: '1px solid var(--crm-success)', color: 'var(--crm-success)' }}>
                                        Прихід: <strong>{receivedQuantity.toFixed(3)} {i.weightType}</strong>
                                      </span>
                                      {i.semiFinishedProductId && (
                                        <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-success-bg)', border: '1px solid var(--crm-success)', color: 'var(--crm-success)' }}>
                                          Вироблено: <strong>{producedQuantity.toFixed(3)} {i.weightType}</strong>
                                        </span>
                                      )}
                                      <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', color: 'var(--crm-danger)' }}>
                                        Реалізація: <strong>{realizedQuantity.toFixed(3)} {i.weightType}</strong>
                                      </span>
                                      <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', color: 'var(--crm-warning)' }}>
                                        Використано: <strong>{usedQuantity.toFixed(3)} {i.weightType}</strong>
                                      </span>
                                      {expectedBeforeCount !== null && (
                                        <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', color: 'var(--crm-text)' }}>
                                          Очікувано перед підрахунком: <strong>{expectedBeforeCount.toFixed(3)} {i.weightType}</strong>
                                        </span>
                                      )}
                                      <span style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', color: 'var(--crm-text)' }}>
                                        Фактично в ревізії: <strong>{actualCount.toFixed(3)} {i.weightType}</strong>
                                      </span>
                                      {countDifference !== null && (
                                        <span style={{ padding: '5px 8px', borderRadius: 6, background: countDifference < 0 ? 'var(--crm-danger-bg)' : countDifference > 0 ? 'var(--crm-success-bg)' : 'var(--crm-surface)', border: `1px solid ${countDifference < 0 ? 'var(--crm-danger)' : countDifference > 0 ? 'var(--crm-success)' : 'var(--crm-border)'}`, color: countDifference < 0 ? 'var(--crm-danger)' : countDifference > 0 ? 'var(--crm-success)' : 'var(--crm-text-muted)' }}>
                                          Розбіжність: <strong>{countDifference > 0 ? '+' : ''}{countDifference.toFixed(3)} {i.weightType}</strong>
                                        </span>
                                      )}
                                    </div>
                                    {displayedMovements.length === 0 ? (
                                      <div style={{ color: 'var(--crm-text-muted)' }}>За період ревізії рухів не знайдено.</div>
                                    ) : (
                                      <div style={{ display: 'grid', gap: 6 }}>
                                        {displayedMovements.map((entry) => {
                                          if (entry.kind === 'writeoff-day') {
                                            const total = entry.movements.reduce((sum, movement) => sum + Math.abs(movement.quantityDelta), 0);
                                            return (
                                              <details key={`writeoff-day-${entry.day}`} style={{ borderLeft: '3px solid var(--crm-danger)', borderRadius: 4, background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '7px 9px' }}>
                                                <summary style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }}>
                                                  <strong>Списання · {new Date(`${entry.day}T00:00:00`).toLocaleDateString('uk-UA')} ({entry.movements.length})</strong>
                                                  <strong style={{ color: 'var(--crm-danger)' }}>−{total.toFixed(3)} {i.weightType}</strong>
                                                </summary>
                                                <div style={{ display: 'grid', gap: 5, marginTop: 8, paddingLeft: 8 }}>
                                                  {entry.movements.map((movement) => (
                                                    <div key={movement.id} style={{ borderTop: '1px solid var(--crm-table-row-border)', paddingTop: 6, fontSize: 12 }}>
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                                        {movement.writeOffId && movement.productId ? (
                                                          <button
                                                            type="button"
                                                            onClick={() => toggleWriteOffDishBreakdown(movement)}
                                                            aria-expanded={expandedWriteOffDishes.has(`${movement.writeOffId}-${movement.productId}`)}
                                                            style={{ border: 0, padding: 0, background: 'transparent', color: 'var(--crm-info)', textAlign: 'left', textDecoration: 'underline', cursor: 'pointer' }}
                                                          >
                                                            {movement.label} {expandedWriteOffDishes.has(`${movement.writeOffId}-${movement.productId}`) ? '▲' : '▼'}
                                                          </button>
                                                        ) : <span>{movement.label}</span>}
                                                        <strong style={{ color: 'var(--crm-danger)', whiteSpace: 'nowrap' }}>{movement.quantityDelta.toFixed(3)} {i.weightType}</strong>
                                                      </div>
                                                      {movement.writeOffId && movement.productId && expandedWriteOffDishes.has(`${movement.writeOffId}-${movement.productId}`) && (() => {
                                                        const key = `${movement.writeOffId}-${movement.productId}`;
                                                        if (writeOffDishLoading === key) return <div style={{ marginTop: 6, color: 'var(--crm-text-muted)' }}>Завантаження страв…</div>;
                                                        if (writeOffDishErrors[key]) return <div role="alert" style={{ marginTop: 6, color: 'var(--crm-danger)' }}>{writeOffDishErrors[key]}</div>;
                                                        const dishRows = writeOffDishRows[key] || [];
                                                        return dishRows.length === 0
                                                          ? <div style={{ marginTop: 6, color: 'var(--crm-text-muted)' }}>Деталізація за стравами недоступна для цього списання.</div>
                                                          : <div style={{ display: 'grid', gap: 4, marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--crm-info)' }}>
                                                            {dishRows.map((dish, dishIndex) => (
                                                              <div key={`${dish.dishId}-${dish.sectionName}-${dishIndex}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                                                <span>{dish.sectionName} · {dish.dishName} ({formatNum(dish.portions, 2)} порц.)</span>
                                                                <strong style={{ whiteSpace: 'nowrap' }}>{formatNum(dish.quantity, 3)} {dish.weightType}</strong>
                                                              </div>
                                                            ))}
                                                          </div>;
                                                      })()}
                                                    </div>
                                                  ))}
                                                </div>
                                              </details>
                                            );
                                          }
                                          const movement = entry.movement;
                                          const isDraft = movement.status.toLowerCase() === 'draft';
                                          const amount = isDraft ? movement.pendingDelta : movement.quantityDelta;
                                          return (
                                            <div key={movement.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', padding: '7px 9px', borderLeft: `3px solid ${isDraft ? 'var(--crm-warning)' : movement.quantityDelta < 0 ? 'var(--crm-danger)' : 'var(--crm-success)'}`, borderRadius: 4, background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
                                              <div>
                                                <strong>{movement.label}</strong>
                                                {isDraft && <span style={{ marginLeft: 7, color: 'var(--crm-warning)', fontSize: 11, fontWeight: 700 }}>ЧЕРНЕТКА · не впливає на залишок</span>}
                                                {movement.notes && <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>{movement.notes}</div>}
                                              </div>
                                              <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {new Date(movement.movementDate).toLocaleDateString('uk-UA')} ·{' '}
                                                <strong style={{ color: isDraft ? 'var(--crm-warning)' : (amount || 0) < 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>
                                                  {isDraft ? 'очікувано ' : ''}{(amount || 0) > 0 ? '+' : ''}{(amount || 0).toFixed(3)} {i.weightType}
                                                </strong>
                                                {!isDraft && <div style={{ color: 'var(--crm-text-muted)', fontSize: 11 }}>залишок: {movement.balanceAfter.toFixed(3)}</div>}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                    {filteredItems.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '18px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                          Нічого не знайдено за пошуком.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Оберіть ревізію зі списку або створіть нову.</div>
          )}
        </div>
      </div>

      {showImport && selected && (
        <div role="dialog" aria-modal="true" aria-labelledby="revision-import-title" style={{ position: 'fixed', inset: 0, zIndex: 1450, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div className="inventory-revision-dialog" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: '20px', borderRadius: '10px', width: 'min(900px, 100%)', maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--crm-shadow-sm)' }}>
            <h3 id="revision-import-title">Імпорт фактичних залишків: {selected.name}</h3>
            <p>Завантажте шаблон саме цієї ревізії. Аркуш «Інструкція для ШІ» містить промпт: передайте файл і текст інвентаризації іншому ШІ, потім завантажте заповнений XLSX.             Заповнюйте лише колонку H: ці кількості <strong>додаються</strong> до вже введених (не замінюють їх). Порожні клітинки нічого не змінюють.</p>
                        <p>Перед збереженням перевірте й виправте кількості для додавання. Імпорт не проводить ревізію. Для позицій із записами ваги додана кількість зберігається окремим записом.</p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
              <button type="button" disabled={importBusy} onClick={downloadImportTemplate} style={btn('primary')}>↓ Шаблон з промптом</button>
              <label>Завантажити заповнений XLSX:
                <input type="file" accept=".xlsx" disabled={importBusy} onChange={event => {
                  const file = event.target.files?.[0];
                  if (file) void previewImportFile(file);
                  event.target.value = '';
                }} style={{ display: 'block', marginTop: '5px' }} />
              </label>
            </div>
            {importBusy && <p>Обробка файла...</p>}
            {importError && <div role="alert" style={{ whiteSpace: 'pre-wrap', color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', padding: '10px', marginBottom: '10px' }}>{importError}</div>}
            {importPreview && (
              <>
                <p><strong>Знайдено заповнених позицій: {importPreview.rows.length}.</strong> Решта позицій залишиться без змін.</p>
                <div style={{ overflow: 'auto', maxHeight: '48vh' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={thStyle}>Тип</th><th style={thStyle}>Позиція</th><th style={thStyle}>Од.</th>                    <th style={thStyle}>Вже введено</th><th style={thStyle}>Додати (редагуйте)</th><th style={thStyle}>Буде разом</th></tr></thead>
                                        <tbody>{importPreview.rows.map(row => {
                                          const added = Number((importValues[row.revisionItemId] || '').trim().replace(',', '.'));
                                          return (
                                            <tr key={row.revisionItemId}>
                                              <td style={tdStyle}>{row.itemType}</td>
                                              <td style={tdStyle}>{row.itemName}</td>
                                              <td style={tdStyle}>{row.unit}</td>
                                              <td style={tdStyle}>{formatNum(row.currentQuantity, 3)}</td>
                                              <td style={tdStyle}><input aria-label={`Додати: ${row.itemName}`} type="text" inputMode="decimal" value={importValues[row.revisionItemId] ?? ''} onChange={event => setImportValues(prev => ({ ...prev, [row.revisionItemId]: event.target.value }))} style={{ width: '120px', padding: '6px' }} /></td>
                                              <td style={{ ...tdStyle, fontWeight: 600 }}>{Number.isFinite(added) && added >= 0 ? formatNum(row.currentQuantity + added, 3) : '—'}</td>
                                            </tr>
                                          );
                                        })}</tbody>
                  </table>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button type="button" disabled={importBusy} onClick={() => { setShowImport(false); setImportPreview(null); }} style={btn('muted')}>Скасувати</button>
              <button type="button" disabled={importBusy || !importPreview} onClick={applyImport} style={btn('success')}>Додати перевірені кількості</button>
            </div>
          </div>
        </div>
      )}

      {selected && !isPostedRevision(selected.status) && showEditorModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobileEditor ? '0' : '20px' }}>
          <div className="inventory-revision-editor" style={{ width: isMobileEditor ? '100vw' : 'min(1600px, 98vw)', height: isMobileEditor ? '94vh' : '90vh', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: isMobileEditor ? 0 : '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="inventory-revision-editor__header" style={{ padding: isMobileEditor ? '8px 10px' : '12px 16px', borderBottom: '1px solid var(--crm-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <strong className="inventory-revision-editor__title" style={{ minWidth: 0, maxWidth: isMobileEditor ? '46%' : '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>
                Введення ревізії: {selected.name}
              </strong>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button onClick={syncMissingItems} disabled={syncingItems} title="Додати нові продукти та Н/Ф у ревізію" style={btn('info', { padding: isMobileEditor ? '8px 10px' : '8px 16px', fontSize: '14px' })}>{syncingItems ? '...' : (isMobileEditor ? '+ Нові' : '+ Нові позиції')}</button>
                <button onClick={refreshExpected} disabled={refreshingExpected} title="Перерахувати очікувані залишки" style={btn('accent', { padding: isMobileEditor ? '8px 10px' : '8px 16px', fontSize: '14px' })}>{refreshingExpected ? '...' : (isMobileEditor ? '↻ Очік.' : '↻ Перерахувати очікуване')}</button>
                <button onClick={handleSaveItems} disabled={saving} style={btn('info', { padding: isMobileEditor ? '8px 10px' : '8px 16px', fontSize: '14px' })}>{saving ? '...' : 'Зберегти'}</button>
                <button onClick={handleCloseEditorModal} style={btn('muted', { padding: isMobileEditor ? '8px 10px' : '8px 16px', fontSize: '14px' })}>Закрити</button>
              </div>
            </div>
            <div className="inventory-revision-editor__search" style={{ padding: isMobileEditor ? '8px 10px' : '10px 16px', borderBottom: '1px solid var(--crm-border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Пошук продукту / Н/Ф / категорії..."
                style={{ width: isMobileEditor ? '68%' : '380px', maxWidth: '100%', minHeight: isMobileEditor ? '44px' : undefined, padding: isMobileEditor ? '8px 10px' : '7px 10px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)', fontSize: isMobileEditor ? '16px' : '14px' }}
              />
              <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>Позицій: {filteredItems.length}</span>
              {syncMessage && <span style={{ color: 'var(--crm-info)', fontSize: '13px' }}>{syncMessage}</span>}
            </div>
            {isMobileEditor && (
              <div className="inventory-revision-editor__tabs" style={{ padding: '6px 10px', borderBottom: '1px solid var(--crm-border)', display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => setEditorMobileTab('input')}
                  style={btn(editorMobileTab === 'input' ? 'accent' : 'muted', { padding: '8px 12px', fontSize: '14px' })}
                >
                  Ввід
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMobileTab('summary')}
                  style={btn(editorMobileTab === 'summary' ? 'accent' : 'muted', { padding: '8px 12px', fontSize: '14px' })}
                >
                  Результат
                </button>
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: isMobileEditor ? '8px 10px' : '12px 16px' }}>
              <table className="inventory-revision-editor__table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: isMobileEditor ? 'fixed' : 'auto', minWidth: isMobileEditor ? '100%' : '1100px' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: isMobileEditor ? '122px' : undefined, whiteSpace: 'nowrap' }}>Назва</th>
                    {(!isMobileEditor || editorMobileTab === 'summary') && (
                      <th style={{ ...thStyle, textAlign: 'right' }}>
                        Мин. ревізія
                        {selected.previousRevisionName || selected.previousRevisionDateTo
                          ? <div style={{ fontSize: 10, fontWeight: 400 }}>
                              {selected.previousRevisionName ? `${selected.previousRevisionName} · ` : ''}
                              {selected.previousRevisionDateTo ? new Date(selected.previousRevisionDateTo).toLocaleDateString('uk-UA') : ''}
                            </div>
                          : null}
                      </th>
                    )}
                    {(!isMobileEditor || editorMobileTab === 'summary') && <th style={{ ...thStyle, textAlign: 'right' }}>Очікується</th>}
                    {(!isMobileEditor || editorMobileTab === 'input') && (
                      <th style={{ ...thStyle, width: isMobileEditor ? '170px' : undefined }}>Додавання (Enter)</th>
                    )}
                    {(!isMobileEditor || editorMobileTab === 'summary' || editorMobileTab === 'input') && (
                      <th style={{ ...thStyle, width: isMobileEditor && editorMobileTab === 'input' ? '84px' : undefined }}>Записи Ваги</th>
                    )}
                    {(!isMobileEditor || editorMobileTab === 'summary') && <th style={{ ...thStyle, textAlign: 'right' }}>Фактично</th>}
                    {!isMobileEditor && <th style={{ ...thStyle, textAlign: 'right' }}>Поточний залишок</th>}
                    {!isMobileEditor && <th style={{ ...thStyle, textAlign: 'right' }}>Розбіжність</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((i) => {
                    const actual = parseNum(actuals[i.id] ?? String(i.actualQuantity));
                    const discrepancy = actual - i.expectedQuantity;
                    const currentBalance = i.productId ? (balanceMap[i.productId] ?? null) : null;
                    const draft = entryDrafts[i.id] || emptyEntryDraft();
                    const formula = formatEntryFormula(i.entries || []);
                    const onEntryEnter: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEntry(i);
                      }
                    };

                    return (
                      <tr key={i.id}>
                        <td className="inventory-revision-editor__item" style={{ ...tdStyle, maxWidth: isMobileEditor ? '122px' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={i.itemName || '—'}>{i.itemName || '—'}</td>
                        {(!isMobileEditor || editorMobileTab === 'summary') && (
                          <td data-label="Попередня ревізія" style={{ ...tdStyle, textAlign: 'right', color: i.previousRevisionActualQuantity == null ? 'var(--crm-text-muted)' : undefined }}>
                            {i.previousRevisionActualQuantity == null ? '—' : i.previousRevisionActualQuantity.toFixed(2)}
                          </td>
                        )}
                        {(!isMobileEditor || editorMobileTab === 'summary') && <td data-label="Очікується" style={{ ...tdStyle, textAlign: 'right' }}>{i.expectedQuantity.toFixed(2)}</td>}
                        {(!isMobileEditor || editorMobileTab === 'input') && (
                          <td className="inventory-revision-editor__entry" data-label="Додати запис ваги" style={tdStyle}>
                            <div className="inventory-revision-editor__entry-grid" style={{ display: 'grid', gridTemplateColumns: isMobileEditor ? 'repeat(2, minmax(0, 1fr))' : '70px 86px 90px auto', gap: isMobileEditor ? '8px' : '6px', alignItems: 'end' }}>
                              <label className="inventory-revision-editor__entry-field">
                                <span>Кількість, шт.</span>
                                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="0"
                                  value={draft.pieceCount}
                                  onChange={(e) => setEntryDrafts((d) => ({ ...d, [i.id]: { ...draft, pieceCount: e.target.value } }))}
                                  onKeyDown={onEntryEnter}
                                />
                              </label>
                              <label className="inventory-revision-editor__entry-field">
                                <span>Вага 1 шт.</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  placeholder="0"
                                  value={draft.pieceWeight}
                                  onChange={(e) => setEntryDrafts((d) => ({ ...d, [i.id]: { ...draft, pieceWeight: e.target.value } }))}
                                  onKeyDown={onEntryEnter}
                                />
                              </label>
                              <label className="inventory-revision-editor__entry-field">
                                <span>Коригування ваги</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  placeholder="0"
                                  value={draft.extraWeight}
                                  onChange={(e) => setEntryDrafts((d) => ({ ...d, [i.id]: { ...draft, extraWeight: e.target.value } }))}
                                  onKeyDown={onEntryEnter}
                                />
                              </label>
                              <button
                                className="inventory-revision-editor__add-entry"
                                type="button"
                                onClick={() => handleAddEntry(i)}
                                disabled={addingEntryId === i.id}
                                style={btn('accent', { padding: isMobileEditor ? '8px 12px' : '6px 10px', whiteSpace: 'nowrap', fontSize: '14px' })}
                              >
                                {addingEntryId === i.id ? 'Додаємо…' : 'Додати запис'}
                              </button>
                            </div>
                          </td>
                        )}
                        {(!isMobileEditor || editorMobileTab === 'summary' || editorMobileTab === 'input') && (
                          <td className="inventory-revision-editor__formula" data-label="Записи ваги" style={{ ...tdStyle, fontSize: '12px', lineHeight: 1.35, maxWidth: isMobileEditor ? '120px' : '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formula}>
                            {formula || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}
                          </td>
                        )}
                        {(!isMobileEditor || editorMobileTab === 'summary') && (
                          <td data-label="Фактично" style={{ ...tdStyle, textAlign: 'right' }}>
                            {actual.toFixed(2)}
                          </td>
                        )}
                        {!isMobileEditor && (
                          <td style={{
                            ...tdStyle, textAlign: 'right', fontWeight: 'bold',
                            color: currentBalance === null ? 'var(--crm-text-muted)' : currentBalance > 0 ? 'var(--crm-success)' : currentBalance < 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)',
                          }}>
                            {currentBalance !== null ? currentBalance.toFixed(2) : '—'}
                          </td>
                        )}
                        {!isMobileEditor && (
                          <td style={{ ...tdStyle, textAlign: 'right', color: discrepancy > 0 ? 'var(--crm-success)' : discrepancy < 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                            {discrepancy.toFixed(2)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={isMobileEditor ? (editorMobileTab === 'input' ? 3 : 4) : 7} style={{ padding: '18px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                        Нічого не знайдено за пошуком.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selected && showOrderAdjustModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="inventory-revision-adjust-dialog" style={{ width: 'min(880px, 96vw)', maxHeight: '86vh', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="inventory-revision-adjust-dialog__header" style={{ padding: '14px 18px', borderBottom: '1px solid var(--crm-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>Коригування за замовленням</strong>
              <button onClick={() => setShowOrderAdjustModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--crm-text-muted)' }}>✕</button>
            </div>
            <div className="inventory-revision-adjust-dialog__body" style={{ padding: '16px 18px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, color: 'var(--crm-text-muted)', fontSize: '13px' }}>
                Оберіть замовлення, продукти зі страв якого потрібно врахувати у фактичному підрахунку — наприклад, якщо ревізію
                рахують не в той день, на який вона запланована, а замовлення випадає між цими датами.
              </p>
              <input
                type="text"
                placeholder="Пошук за назвою події, клієнтом або майданчиком..."
                value={orderQuery}
                onChange={(e) => { setOrderQuery(e.target.value); loadOrdersForAdjustment(e.target.value); }}
                style={{ padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
              />
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                {loadingOrders && <div style={{ padding: '12px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Завантаження...</div>}
                {!loadingOrders && orderResults.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Замовлень не знайдено.</div>
                )}
                {!loadingOrders && orderResults.map((o) => (
                  <div
                    key={o.id}
                    onClick={() => setSelectedOrderId(o.id)}
                    style={{
                      padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--crm-table-row-border)',
                      background: selectedOrderId === o.id ? 'var(--crm-info-bg)' : undefined,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--crm-text)' }}>{o.eventName || o.customerName || `Замовлення #${o.id}`}</div>
                    <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                      {new Date(o.eventDate).toLocaleDateString('uk-UA')} · {o.customerName || '—'} · {o.eventVenue || '—'} · {o.guestCount} гостей
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" checked={orderDirection === 'subtract'} onChange={() => setOrderDirection('subtract')} />
                  Відняти (замовлення вже фактично забрало продукти зі складу)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" checked={orderDirection === 'add'} onChange={() => setOrderDirection('add')} />
                  Додати (порахували раніше, ніж відбудеться замовлення)
                </label>
              </div>

              {selectedOrderId && (
                <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: 'var(--crm-surface-alt)', fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)' }}>
                    Попередній перегляд {orderDirection === 'subtract' ? '(буде віднято)' : '(буде додано)'}
                  </div>
                  {loadingPreview && <div style={{ padding: '14px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Розрахунок...</div>}
                  {!loadingPreview && preview && preview.rows.length === 0 && !preview.semiFinishedTotals?.length && (
                    <div style={{ padding: '14px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>У цьому замовленні немає інгредієнтів.</div>
                  )}
                  {!loadingPreview && preview && (
                    <SemiFinishedAdjustmentPreview totals={preview.semiFinishedTotals ?? []} />
                  )}
                  {!loadingPreview && preview && preview.rows.length > 0 && (
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Розділ / Страва</th>
                            <th style={thStyle}>Продукт</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((r, idx) => (
                            <tr key={`${r.dishId}-${r.productId}-${idx}`}>
                              <td style={tdStyle}>
                                <div style={{ color: 'var(--crm-text-muted)', fontSize: '11px' }}>{r.sectionName}</div>
                                <div>{r.dishName} <span style={{ color: 'var(--crm-text-muted)' }}>× {r.portions}</span></div>
                              </td>
                              <td style={tdStyle}>{r.productName}{r.categoryName ? <span style={{ color: 'var(--crm-text-muted)' }}> ({r.categoryName})</span> : null}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: r.quantity < 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>
                                {r.quantity > 0 ? '+' : ''}{r.quantity.toFixed(3)} {r.weightType}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ padding: '8px 12px', background: 'var(--crm-surface-alt)', borderTop: '1px solid var(--crm-border)', fontWeight: 600, fontSize: '12px', color: 'var(--crm-text)' }}>
                        Разом по продуктах:
                      </div>
                      <div style={{ padding: '4px 12px 10px' }}>
                        {preview.totals.map((t) => (
                          <div key={t.productId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '3px 0', borderBottom: '1px dashed var(--crm-table-row-border)' }}>
                            <span>{t.productName}{t.categoryName ? <span style={{ color: 'var(--crm-text-muted)' }}> ({t.categoryName})</span> : null}</span>
                            <strong style={{ color: t.quantity < 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>
                              {t.quantity > 0 ? '+' : ''}{t.quantity.toFixed(3)} {t.weightType}
                            </strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--crm-border)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowOrderAdjustModal(false)} style={btn('muted')}>Скасувати</button>
              <button
                onClick={handleApplyOrder}
                disabled={!selectedOrderId || applyingOrder}
                style={btn(!selectedOrderId || applyingOrder ? 'muted' : 'warning', { cursor: !selectedOrderId || applyingOrder ? 'not-allowed' : 'pointer' })}
              >
                {applyingOrder ? 'Застосування...' : (orderDirection === 'subtract' ? 'Відняти продукти та Н/Ф' : 'Додати продукти та Н/Ф')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && showReceiptAdjustModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="inventory-revision-adjust-dialog" style={{ width: 'min(880px, 96vw)', maxHeight: '86vh', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="inventory-revision-adjust-dialog__header" style={{ padding: '14px 18px', borderBottom: '1px solid var(--crm-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>Коригування за накладною</strong>
              <button onClick={() => setShowReceiptAdjustModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--crm-text-muted)' }}>✕</button>
            </div>
            <div className="inventory-revision-adjust-dialog__body" style={{ padding: '16px 18px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, color: 'var(--crm-text-muted)', fontSize: '13px' }}>
                Оберіть накладну надходження, продукти якої потрібно врахувати у фактичному підрахунку — наприклад, якщо продукти
                вже приїхали і потрапили до незваженого залишку, а вам потрібно відняти їх з ревізії по цій конкретній накладній.
              </p>
              <input
                type="text"
                placeholder="№, постачальник, примітка або дата (дд.мм.рррр)..."
                value={receiptQuery}
                onChange={(e) => { setReceiptQuery(e.target.value); loadReceiptsForAdjustment(e.target.value); }}
                style={{ padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
              />
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                {loadingReceipts && <div style={{ padding: '12px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Завантаження...</div>}
                {!loadingReceipts && receiptResults.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Накладних не знайдено.</div>
                )}
                {!loadingReceipts && receiptResults.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => setSelectedReceiptId(r.id)}
                    style={{
                      padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--crm-table-row-border)',
                      background: selectedReceiptId === r.id ? 'var(--crm-success-bg)' : undefined,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--crm-text)' }}>
                      {r.invoiceNumber ? `Накладна №${r.invoiceNumber}` : `Накладна #${r.id}`} · {r.supplierName || 'без постачальника'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                      {new Date(r.date).toLocaleDateString('uk-UA')} · {r.itemCount} позицій · {r.totalAmount.toFixed(2)} грн
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" checked={receiptDirection === 'subtract'} onChange={() => setReceiptDirection('subtract')} />
                  Відняти (продукти по накладній вже потрапили до незваженого залишку)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" checked={receiptDirection === 'add'} onChange={() => setReceiptDirection('add')} />
                  Додати (продукти по накладній ще не враховані у фактичному залишку)
                </label>
              </div>

              {selectedReceiptId && (
                <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: 'var(--crm-surface-alt)', fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)' }}>
                    Попередній перегляд {receiptDirection === 'subtract' ? '(буде віднято)' : '(буде додано)'}
                  </div>
                  {loadingReceiptPreview && <div style={{ padding: '14px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Розрахунок...</div>}
                  {!loadingReceiptPreview && receiptPreview && receiptPreview.rows.length === 0 && (
                    <div style={{ padding: '14px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>У цій накладній немає продуктів, прив'язаних до складу.</div>
                  )}
                  {!loadingReceiptPreview && receiptPreview && receiptPreview.rows.length > 0 && (
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Продукт</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptPreview.rows.map((r) => (
                            <tr key={r.productId}>
                              <td style={tdStyle}>{r.productName}{r.categoryName ? <span style={{ color: 'var(--crm-text-muted)' }}> ({r.categoryName})</span> : null}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: r.quantity < 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>
                                {r.quantity > 0 ? '+' : ''}{r.quantity.toFixed(3)} {r.weightType}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--crm-border)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowReceiptAdjustModal(false)} style={btn('muted')}>Скасувати</button>
              <button
                onClick={handleApplyReceipt}
                disabled={!selectedReceiptId || applyingReceipt}
                style={btn(!selectedReceiptId || applyingReceipt ? 'muted' : 'success', { cursor: !selectedReceiptId || applyingReceipt ? 'not-allowed' : 'pointer' })}
              >
                {applyingReceipt ? 'Застосування...' : (receiptDirection === 'subtract' ? 'Відняти продукти' : 'Додати продукти')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryRevisionsPage;
